/**
 * DailyReportView - the engineer role's daily field report capture + history.
 *
 * Structured form built to the team's real email template:
 *   Header  - Project (GLOBAL code), Customer, Site, PM, Date, Shift, Hours,
 *             Down time. Auto-filled from GET /api/engineer/daily-reports/prefill
 *             (the engineer's current assignment) where possible; all editable.
 *   Body    - repeatable ZONE work sections (General/Base/Clear/other), each a
 *             list of { time(hrs), description } rows. Add/remove sections+rows.
 *   Units   - optional production-units table { Style, Description, Units ran }.
 *   Open    - free-text open items.
 *   Status  - Green / Yellow / Red (drives the subject-line flag).
 *
 * Submit POSTs to /api/engineer/daily-reports (engineer identity is server-side,
 * never trusted from the body). History below lists this engineer's own past
 * reports. If the account isn't linked ({linked:false}) we show the same
 * friendly "ask an admin to link you" card as the other engineer views.
 *
 * Desktop layout, Montserrat, inline styles - matches MyScheduleView /
 * MyProjectView conventions.
 */
import { useEffect, useState, useCallback } from 'react';

const GREEN = '#1B5E20';
const CARD = {
  background: '#fff',
  border: '1px solid #ECECEC',
  borderRadius: 12,
  boxShadow: '0 1px 4px rgba(0,0,0,0.04)',
  padding: '18px 20px',
  marginBottom: 16,
};
const LABEL = {
  fontSize: 9.5,
  fontWeight: 800,
  color: '#999',
  letterSpacing: 1,
  textTransform: 'uppercase',
  marginBottom: 4,
  display: 'block',
};
const INPUT = {
  width: '100%',
  boxSizing: 'border-box',
  fontFamily: "'Montserrat', sans-serif",
  fontSize: 12.5,
  color: '#0D0D0D',
  padding: '8px 10px',
  border: '1px solid #DDD',
  borderRadius: 8,
  background: '#fff',
  outline: 'none',
};
const SECTION_TITLE = { fontSize: 13, fontWeight: 800, color: '#0D0D0D', margin: '0 0 12px' };
const BTN = {
  fontFamily: "'Montserrat', sans-serif",
  fontSize: 12,
  fontWeight: 700,
  border: 'none',
  borderRadius: 8,
  padding: '8px 14px',
  cursor: 'pointer',
};
const BTN_PRIMARY = { ...BTN, background: GREEN, color: '#fff' };
const BTN_GHOST = { ...BTN, background: '#F2F2F2', color: '#333' };
const BTN_LINK = { ...BTN, background: 'transparent', color: '#B71C1C', padding: '4px 6px' };

const SHIFTS = ['1st', '2nd'];
const STATUSES = [
  { value: 'Green', color: '#1B5E20', bg: '#E8F5E9' },
  { value: 'Yellow', color: '#8D6E00', bg: '#FFF8E1' },
  { value: 'Red', color: '#B71C1C', bg: '#FFEBEE' },
];
// Suggested zone names from the real reports - the name field is free-text but
// these seed the datalist so common zones are one keystroke.
const ZONE_SUGGESTIONS = ['General', 'Base', 'Clear', 'Mezzanine', 'Conveyor', 'Controls', 'Startup'];

const todayIso = () => new Date().toISOString().slice(0, 10);
const emptyRow = () => ({ time: '', description: '' });
const emptySection = (name = 'General') => ({ name, rows: [emptyRow()] });
const emptyUnit = () => ({ style: '', description: '', units: '' });

const fmtUsDate = (iso) => {
  if (!iso) return '-';
  const [y, m, d] = String(iso).slice(0, 10).split('-');
  if (!y) return '-';
  return `${m}/${d}/${y}`;
};
const statusMeta = (v) => STATUSES.find((s) => s.value === v) || STATUSES[0];

// ── Not-linked guidance card (shared shape with My Schedule / My Project) ──
function NotLinkedCard({ message }) {
  return (
    <div style={{ fontFamily: "'Montserrat', sans-serif", display: 'flex', justifyContent: 'center', paddingTop: 40 }}>
      <div
        style={{
          maxWidth: 460,
          textAlign: 'center',
          background: '#FFF8E1',
          border: '1px solid #FFE082',
          borderRadius: 12,
          padding: '28px 26px',
        }}
      >
        <div style={{ fontSize: 11, fontWeight: 800, color: '#8D6E00', letterSpacing: 1.5, textTransform: 'uppercase', marginBottom: 8 }}>
          Account not linked
        </div>
        <div style={{ fontSize: 17, fontWeight: 800, color: '#5D4A00', marginBottom: 10 }}>Can't file a report yet</div>
        <p style={{ fontSize: 12.5, color: '#6D5A1A', lineHeight: 1.6, margin: 0 }}>
          {message || 'Your account is not linked to an engineer record yet. Ask an admin to link you in Team & Access.'}
        </p>
      </div>
    </div>
  );
}

// ── One report in the history list (read-only, expandable summary) ──
function HistoryRow({ r }) {
  const [open, setOpen] = useState(false);
  const meta = statusMeta(r.status);
  const sections = Array.isArray(r.sections) ? r.sections : [];
  const units = Array.isArray(r.units) ? r.units : [];
  return (
    <div style={{ borderBottom: '1px solid #EEE' }}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        style={{
          width: '100%',
          textAlign: 'left',
          background: 'transparent',
          border: 'none',
          cursor: 'pointer',
          padding: '12px 4px',
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          fontFamily: "'Montserrat', sans-serif",
        }}
      >
        <span style={{ fontSize: 12.5, fontWeight: 700, color: '#0D0D0D', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
          {fmtUsDate(r.report_date)}
        </span>
        <span
          style={{
            fontSize: 12.5,
            color: '#333',
            flex: 1,
            minWidth: 0,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {r.global_project_number || r.project_label || '-'}
          {r.customer ? ` · ${r.customer}` : ''}
        </span>
        <span style={{ fontSize: 10, fontWeight: 800, color: meta.color, background: meta.bg, borderRadius: 20, padding: '3px 10px' }}>
          {r.status || 'Green'}
        </span>
        <span style={{ fontSize: 10, color: r.emailed_at ? GREEN : '#AAA', whiteSpace: 'nowrap' }}>
          {r.emailed_at ? 'Emailed' : 'Not emailed'}
        </span>
        <span style={{ fontSize: 11, color: '#999' }}>{open ? '▾' : '▸'}</span>
      </button>
      {open && (
        <div style={{ padding: '0 4px 14px', fontSize: 12.5, color: '#333' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px,1fr))', gap: 10, marginBottom: 12 }}>
            <div>
              <b>Site:</b> {r.site_location || '-'}
            </div>
            <div>
              <b>PM:</b> {r.pm || '-'}
            </div>
            <div>
              <b>Shift:</b> {r.shift || '-'}
            </div>
            <div>
              <b>Hours:</b> {r.hours != null ? r.hours : '-'}
            </div>
            <div>
              <b>Down time:</b> {r.downtime || '-'}
            </div>
          </div>
          {sections.map((s, i) => (
            <div key={i} style={{ marginBottom: 10 }}>
              <div
                style={{ fontWeight: 800, color: GREEN, fontSize: 11.5, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4 }}
              >
                {s.name || 'Section'}
              </div>
              {(Array.isArray(s.rows) ? s.rows : []).map((row, j) => (
                <div key={j} style={{ display: 'flex', gap: 10, padding: '2px 0' }}>
                  <span style={{ color: '#888', fontVariantNumeric: 'tabular-nums', minWidth: 40 }}>
                    {row.time != null && row.time !== '' ? `${row.time}h` : '-'}
                  </span>
                  <span>{row.description || ''}</span>
                </div>
              ))}
            </div>
          ))}
          {units.length > 0 && (
            <div style={{ marginBottom: 6 }}>
              <div
                style={{ fontWeight: 800, color: GREEN, fontSize: 11.5, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4 }}
              >
                Production Units
              </div>
              {units.map((u, k) => (
                <div key={k} style={{ display: 'flex', gap: 10, padding: '2px 0' }}>
                  <span style={{ minWidth: 120, fontWeight: 600 }}>{u.style || '-'}</span>
                  <span style={{ flex: 1 }}>{u.description || ''}</span>
                  <span style={{ color: '#888', fontVariantNumeric: 'tabular-nums' }}>
                    {u.units != null && u.units !== '' ? u.units : '-'}
                  </span>
                </div>
              ))}
            </div>
          )}
          {r.open_items && (
            <div style={{ marginTop: 8 }}>
              <div
                style={{ fontWeight: 800, color: GREEN, fontSize: 11.5, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4 }}
              >
                Open Items
              </div>
              <div style={{ whiteSpace: 'pre-wrap' }}>{r.open_items}</div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function DailyReportView() {
  const [load, setLoad] = useState({ loading: true, error: null, linked: null, message: null });
  const [header, setHeader] = useState({
    projectLabel: '',
    globalProjectNumber: '',
    customer: '',
    siteLocation: '',
    pm: '',
    reportDate: todayIso(),
    shift: '1st',
    hours: '',
    downtime: '',
  });
  const [sections, setSections] = useState([emptySection('General')]);
  const [units, setUnits] = useState([]);
  const [openItems, setOpenItems] = useState('');
  const [status, setStatus] = useState('Green');

  const [reports, setReports] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [flash, setFlash] = useState(null); // { kind:'ok'|'err', text }

  // Load prefill (header defaults) + existing history in parallel.
  const loadHistory = useCallback(() => {
    fetch('/api/engineer/daily-reports')
      .then((r) => r.json())
      .then((d) => {
        if (d && d.linked !== false) setReports(d.reports || []);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    let alive = true;
    Promise.all([
      fetch('/api/engineer/daily-reports/prefill').then((r) => r.json()),
      fetch('/api/engineer/daily-reports').then((r) => r.json()),
    ])
      .then(([pre, hist]) => {
        if (!alive) return;
        if (pre && pre.linked === false) {
          setLoad({ loading: false, error: null, linked: false, message: pre.message });
          return;
        }
        if (pre && pre.prefill) {
          const p = pre.prefill;
          setHeader((h) => ({
            ...h,
            projectLabel: p.projectLabel || '',
            globalProjectNumber: p.globalProjectNumber || '',
            customer: p.customer || '',
            siteLocation: p.siteLocation || '',
            pm: p.pm || '',
          }));
        }
        if (hist && hist.reports) setReports(hist.reports);
        setLoad({ loading: false, error: null, linked: true, message: null });
      })
      .catch((err) => {
        if (alive) setLoad({ loading: false, error: err.message || 'Failed to load', linked: null, message: null });
      });
    return () => {
      alive = false;
    };
  }, []);

  const setH = (k) => (e) => setHeader((h) => ({ ...h, [k]: e.target.value }));

  // ── Section / row mutators ──
  const addSection = () => setSections((s) => [...s, emptySection('')]);
  const removeSection = (i) => setSections((s) => (s.length > 1 ? s.filter((_, idx) => idx !== i) : s));
  const setSectionName = (i) => (e) => setSections((s) => s.map((sec, idx) => (idx === i ? { ...sec, name: e.target.value } : sec)));
  const addRow = (i) => setSections((s) => s.map((sec, idx) => (idx === i ? { ...sec, rows: [...sec.rows, emptyRow()] } : sec)));
  const removeRow = (i, j) =>
    setSections((s) =>
      s.map((sec, idx) => (idx === i ? { ...sec, rows: sec.rows.length > 1 ? sec.rows.filter((_, r) => r !== j) : sec.rows } : sec))
    );
  const setRow = (i, j, k) => (e) =>
    setSections((s) =>
      s.map((sec, idx) => (idx === i ? { ...sec, rows: sec.rows.map((row, r) => (r === j ? { ...row, [k]: e.target.value } : row)) } : sec))
    );

  // ── Units mutators ──
  const addUnit = () => setUnits((u) => [...u, emptyUnit()]);
  const removeUnit = (i) => setUnits((u) => u.filter((_, idx) => idx !== i));
  const setUnit = (i, k) => (e) => setUnits((u) => u.map((row, idx) => (idx === i ? { ...row, [k]: e.target.value } : row)));

  const submit = async () => {
    setSubmitting(true);
    setFlash(null);
    try {
      const payload = {
        ...header,
        status,
        openItems,
        sections: sections
          .map((s) => ({ name: s.name, rows: s.rows.filter((r) => r.time !== '' || (r.description || '').trim() !== '') }))
          .filter((s) => (s.name || '').trim() !== '' || s.rows.length > 0),
        units: units.filter((u) => (u.style || '').trim() !== '' || (u.description || '').trim() !== '' || u.units !== ''),
      };
      const res = await fetch('/api/engineer/daily-reports', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const e = await res.json().catch(() => ({}));
        throw new Error(e.error || `Request failed (${res.status})`);
      }
      await res.json();
      setFlash({ kind: 'ok', text: 'Report filed.' });
      // Reset the body but keep header (same engineer, likely same project).
      setSections([emptySection('General')]);
      setUnits([]);
      setOpenItems('');
      setStatus('Green');
      loadHistory();
    } catch (err) {
      setFlash({ kind: 'err', text: err.message || 'Failed to file report.' });
    } finally {
      setSubmitting(false);
    }
  };

  if (load.loading) {
    return (
      <div style={{ padding: 40, textAlign: 'center', color: '#888', fontFamily: "'Montserrat', sans-serif", fontSize: 13 }}>Loading…</div>
    );
  }
  if (load.error) {
    return (
      <div style={{ padding: 40, textAlign: 'center', color: '#B71C1C', fontFamily: "'Montserrat', sans-serif", fontSize: 13 }}>
        Couldn't load the report form. Please try again.
      </div>
    );
  }
  if (load.linked === false) {
    return <NotLinkedCard message={load.message} />;
  }

  return (
    <div style={{ fontFamily: "'Montserrat', sans-serif", maxWidth: 900 }}>
      <div style={{ marginBottom: 16 }}>
        <div style={{ fontSize: 10, fontWeight: 700, color: GREEN, letterSpacing: 2, textTransform: 'uppercase' }}>
          Engineer · Daily Report
        </div>
        <p style={{ fontSize: 12.5, color: '#555', margin: '4px 0 0' }}>
          File your daily field report. The header is pre-filled from your current assignment - edit anything that's off.
        </p>
      </div>

      {/* datalist seeds common zone names */}
      <datalist id="zone-suggestions">
        {ZONE_SUGGESTIONS.map((z) => (
          <option key={z} value={z} />
        ))}
      </datalist>

      {/* ── Header ── */}
      <div style={CARD}>
        <div style={SECTION_TITLE}>Report Header</div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 14 }}>
          <div>
            <label style={LABEL}>Project (GLOBAL #)</label>
            <input style={INPUT} value={header.globalProjectNumber} onChange={setH('globalProjectNumber')} placeholder="e.g. 12345" />
          </div>
          <div>
            <label style={LABEL}>Project Label</label>
            <input style={INPUT} value={header.projectLabel} onChange={setH('projectLabel')} placeholder="Project name" />
          </div>
          <div>
            <label style={LABEL}>Customer</label>
            <input style={INPUT} value={header.customer} onChange={setH('customer')} />
          </div>
          <div>
            <label style={LABEL}>Site Location</label>
            <input style={INPUT} value={header.siteLocation} onChange={setH('siteLocation')} />
          </div>
          <div>
            <label style={LABEL}>PM / PE / IE / SE</label>
            <input style={INPUT} value={header.pm} onChange={setH('pm')} placeholder="Project / project engineer" />
          </div>
          <div>
            <label style={LABEL}>Date</label>
            <input type="date" style={INPUT} value={header.reportDate} onChange={setH('reportDate')} />
          </div>
          <div>
            <label style={LABEL}>Shift</label>
            <select style={INPUT} value={header.shift} onChange={setH('shift')}>
              {SHIFTS.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label style={LABEL}>Hours</label>
            <input type="number" min="0" step="0.5" style={INPUT} value={header.hours} onChange={setH('hours')} />
          </div>
          <div style={{ gridColumn: '1 / -1' }}>
            <label style={LABEL}>Down time (hrs + reason)</label>
            <input style={INPUT} value={header.downtime} onChange={setH('downtime')} placeholder="e.g. 1.5h - waiting on parts" />
          </div>
        </div>
      </div>

      {/* ── Zone work sections ── */}
      <div style={CARD}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={SECTION_TITLE}>Work by Zone</div>
          <button type="button" style={BTN_GHOST} onClick={addSection}>
            + Add zone
          </button>
        </div>
        {sections.map((sec, i) => (
          <div key={i} style={{ border: '1px solid #EEE', borderRadius: 10, padding: 14, marginTop: 12 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
              <input
                style={{ ...INPUT, fontWeight: 700, maxWidth: 260 }}
                value={sec.name}
                onChange={setSectionName(i)}
                placeholder="Zone name (e.g. General)"
                list="zone-suggestions"
              />
              {sections.length > 1 && (
                <button type="button" style={BTN_LINK} onClick={() => removeSection(i)}>
                  Remove zone
                </button>
              )}
            </div>
            {sec.rows.map((row, j) => (
              <div key={j} style={{ display: 'flex', gap: 10, marginBottom: 8, alignItems: 'center' }}>
                <input
                  type="number"
                  min="0"
                  step="0.25"
                  style={{ ...INPUT, width: 90, flex: '0 0 auto' }}
                  value={row.time}
                  onChange={setRow(i, j, 'time')}
                  placeholder="hrs"
                />
                <input
                  style={{ ...INPUT, flex: 1 }}
                  value={row.description}
                  onChange={setRow(i, j, 'description')}
                  placeholder="What was done"
                />
                {sec.rows.length > 1 && (
                  <button type="button" style={{ ...BTN_LINK, flex: '0 0 auto' }} onClick={() => removeRow(i, j)} title="Remove line">
                    ✕
                  </button>
                )}
              </div>
            ))}
            <button type="button" style={{ ...BTN_GHOST, marginTop: 2 }} onClick={() => addRow(i)}>
              + Add line
            </button>
          </div>
        ))}
      </div>

      {/* ── Production units (optional) ── */}
      <div style={CARD}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={SECTION_TITLE}>
            Production Units <span style={{ fontWeight: 500, color: '#999', fontSize: 11 }}>(optional)</span>
          </div>
          <button type="button" style={BTN_GHOST} onClick={addUnit}>
            + Add row
          </button>
        </div>
        {units.length === 0 ? (
          <p style={{ fontSize: 12, color: '#999', margin: '10px 0 0' }}>
            No production units recorded. Add a row if you ran product today.
          </p>
        ) : (
          <div style={{ marginTop: 12 }}>
            <div style={{ display: 'flex', gap: 10, marginBottom: 6 }}>
              <span style={{ ...LABEL, flex: 1, margin: 0 }}>Style</span>
              <span style={{ ...LABEL, flex: 2, margin: 0 }}>Description</span>
              <span style={{ ...LABEL, width: 100, margin: 0 }}>Units ran</span>
              <span style={{ width: 28 }} />
            </div>
            {units.map((u, i) => (
              <div key={i} style={{ display: 'flex', gap: 10, marginBottom: 8, alignItems: 'center' }}>
                <input style={{ ...INPUT, flex: 1 }} value={u.style} onChange={setUnit(i, 'style')} />
                <input style={{ ...INPUT, flex: 2 }} value={u.description} onChange={setUnit(i, 'description')} />
                <input
                  type="number"
                  min="0"
                  step="1"
                  style={{ ...INPUT, width: 100, flex: '0 0 auto' }}
                  value={u.units}
                  onChange={setUnit(i, 'units')}
                />
                <button type="button" style={{ ...BTN_LINK, flex: '0 0 auto', width: 28 }} onClick={() => removeUnit(i)} title="Remove row">
                  ✕
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ── Open items + status ── */}
      <div style={CARD}>
        <div style={SECTION_TITLE}>Open Items & Status</div>
        <label style={LABEL}>Open items</label>
        <textarea
          style={{ ...INPUT, minHeight: 80, resize: 'vertical' }}
          value={openItems}
          onChange={(e) => setOpenItems(e.target.value)}
          placeholder="Outstanding issues, follow-ups, blockers…"
        />
        <div style={{ marginTop: 14 }}>
          <label style={LABEL}>Status</label>
          <div style={{ display: 'flex', gap: 10 }}>
            {STATUSES.map((s) => {
              const active = status === s.value;
              return (
                <button
                  key={s.value}
                  type="button"
                  onClick={() => setStatus(s.value)}
                  style={{
                    ...BTN,
                    background: active ? s.bg : '#fff',
                    color: active ? s.color : '#888',
                    border: `2px solid ${active ? s.color : '#DDD'}`,
                    minWidth: 96,
                  }}
                >
                  {s.value}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* ── Submit ── */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 28 }}>
        <button
          type="button"
          style={{
            ...BTN_PRIMARY,
            opacity: submitting ? 0.6 : 1,
            cursor: submitting ? 'default' : 'pointer',
            padding: '10px 22px',
            fontSize: 13,
          }}
          onClick={submit}
          disabled={submitting}
        >
          {submitting ? 'Filing…' : 'Submit Daily Report'}
        </button>
        {flash && <span style={{ fontSize: 12.5, fontWeight: 600, color: flash.kind === 'ok' ? GREEN : '#B71C1C' }}>{flash.text}</span>}
      </div>

      {/* ── History ── */}
      <div style={{ margin: '0 0 10px', fontSize: 13, fontWeight: 800, color: '#0D0D0D' }}>Your Recent Reports</div>
      {reports.length > 0 ? (
        <div style={{ ...CARD, padding: '4px 16px' }}>
          {reports.map((r) => (
            <HistoryRow key={r.id} r={r} />
          ))}
        </div>
      ) : (
        <div style={{ ...CARD, textAlign: 'center', color: '#999', fontSize: 12.5, padding: 28 }}>
          No reports filed yet. Your submitted reports will appear here.
        </div>
      )}
    </div>
  );
}
